import HttpStatus from 'components/Common/HttpStatus'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import React, { ComponentProps } from 'react'
import { connect } from 'react-redux'
import { Switch, Route as BaseRoute, RouteComponentProps } from 'react-router'
import Route from './TrackedRoute'
import loadable from 'components/utils/lazyImport'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE_SLUG,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_LAST_MINUTE_SLUG,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
  PRODUCT_TYPE_HOME_AND_VILLAS_SLUG,
} from 'constants/offer'
import { TYPE_OF_PAGE_DESTINATION, TYPE_OF_PAGE_HOLIDAY } from 'constants/landingPage'
import { TYPE_PAGE_LIMIT } from 'constants/search'
import { isCoverGeniusEnabled } from 'lib/insurance/insuranceHelpers'
import AuthRoute from './AuthRoute'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import { FRONT_PAGE_MAP_VIEW_OFFERS_TYPES } from 'constants/offerList'
import TripPlannerRoutes from 'tripPlanner/TripPlannerRoutes'
import { LUXURY_ESCAPES } from 'constants/brands'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import BusinessTravellerPageRoutes from 'businessTraveller/routes/BusinessTravellerPageRoutes'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import AgentHubGuestPageRoutes from 'agentHub/routes/AgentHubGuestPageRoutes'
import { useAppSelector } from 'hooks/reduxHooks'
import { LoadableComponent } from '@loadable/component'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getStandaloneAirportTransfersEnabled } from 'selectors/airportTransferSelectors'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'

const DestinationLandingPage = loadable(() => import(/* webpackChunkName: "DestinationLandingPage" */ 'components/Pages/DestinationLandingPage/DestinationLandingPage'))
const CruiseLocationLandingPage = loadable(() => import(/* webpackChunkName: "CruiseLocationLandingPage" */ 'components/Cruises/CruiseSEOpage/CruiseLocationLandingPage'))
const CruiseShipsLandingPage = loadable(() => import(/* webpackChunkName: "CruiseShipsLandingPage" */ 'components/Cruises/CruiseSEOpage/CruiseShipsLandingPage'))
const HotelSearchFullScreenPage = loadable(() => import(/* webpackChunkName: "HotelSearchFullScreenPage" */ 'components/Pages/HotelSearchPage/HotelSearchFullScreenPage'))
const HotelSearchPage = loadable(() => import(/* webpackChunkName: "HotelSearchPage" */ 'components/Pages/HotelSearchPage/HotelSearchPage'))
const HotelSearchMapPage = loadable(() => import(/* webpackChunkName: "HotelSearchMapPage" */ 'components/Pages/HotelSearchPage/HotelSearchMapPage'))
const SupportPage = loadable(() => import(/* webpackChunkName: "SupportPage" */ 'components/Pages/SupportPage/SupportPage'))
const VideoChatPage = loadable(() => import(/* webpackChunkName: "VideoChatPage" */ 'components/Pages/VideoChatPage'))
const AboutUsPage = loadable(() => import(/* webpackChunkName: "AboutUsPage" */ 'components/Pages/AboutUsPage'))
const CareersPage = loadable(() => import(/* webpackChunkName: "CareersPage" */ 'components/Pages/CareersPage'))
const StaticPage = loadable(() => import(/* webpackChunkName: "StaticPage" */ 'components/Pages/StaticPage'))
const BestPricePage = loadable(() => import(/* webpackChunkName: "BestPricePage" */ 'components/Pages/BestPricePage'))
const FlightsLandingPage = loadable(() => import(/* webpackChunkName: "FlightsLandingPage" */ 'components/Pages/PromotionalPages/FlightsLandingPage'))
const FlightsSearchLandingPage = loadable(() => import(/* webpackChunkName: "FlightsSearchLandingPage" */ 'components/Pages/PromotionalPages/FlightsLandingPage/FlightsSearchLandingPage'))
const FlightsSearchPage = loadable(() => import(/* webpackChunkName: "FlightsSearchPage" */ 'components/Flights/FlightsSearchPage'))
const FlightsSearchResultsPage = loadable(() => import(/* webpackChunkName: "FlightsSearchResultsPage" */ 'components/Flights/FlightsSearchResultsPage'))
const FlightsSearchMultiCityResultsPage = loadable(() => import(/* webpackChunkName: "FlightsSearchMultiCityResultsPage" */ 'components/Flights/FlightsSearchMultiCityResultsPage'))
const LegalPages = loadable(() => import(/* webpackChunkName: "LegalPages" */ 'components/Pages/LegalPages'))
const ReferPage = loadable(() => import(/* webpackChunkName: "ReferPage" */ 'marketing/pages/Refer'))
const NewsPage = loadable(() => import(/* webpackChunkName: "NewsPage" */ 'components/Pages/NewsPage'))
const FrequentFlyerPage = loadable(() => import(/* webpackChunkName: "FrequentFlyerPage" */ 'components/Pages/FrequentFlyerPage'))
const MyEscapesPage = loadable(() => import(/* webpackChunkName: "MyEscapesPage" */ 'components/Pages/MyEscapesPage'))
const UserReviews = loadable(() => import(/* webpackChunkName: "UserReviews" */ 'components/Pages/UserReviews'))
const OrderPage = loadable(() => import(/* webpackChunkName: "OrderPage" */ 'components/Pages/MyEscapesPage/OrderPage'))
const AirportTransferPage = loadable(() => import(/* webpackChunkName: "OrderPage" */ 'components/Pages/MyEscapesPage/AirportTransfers'))
const PreferenceCentrePage = loadable(() => import(/* webpackChunkName: "PreferenceCentrePage" */ 'components/Pages/PreferenceCentrePage'))
const MySubscriptionsPage = loadable(() => import(/* webpackChunkName: "MySubscriptionsPage" */ 'components/Pages/MySubscriptionsPage'))
const MyWLSubscriptionsPage = loadable(() => import(/* webpackChunkName: "MySubscriptionsPage" */ 'components/Pages/MyWLSubscriptionsPage'))
const AccountDetailsPage = loadable(() => import(/* webpackChunkName: "AccountDetailsPage" */ 'components/Pages/AccountDetailsPage'))
const AccountDetailsTravellerPage = loadable(() => import(/* webpackChunkName: "AccountDetailsTravellerPage" */ 'components/Pages/AccountDetailsTravellerPage'))
const CreditsAndPaymentPage = loadable(() => import(/* webpackChunkName: "CreditsPage" */ 'components/Pages/CreditsPage/CreditsAndPaymentPage'))
const TourTravellerInfoPage = loadable(() => import(/* webpackChunkName: "TourTravellerInfoPage" */ 'components/Pages/TourTravellerInfo/TourTravellerInfoPage'))
const GiftCardConfirmationPage = loadable(() => import(/* webpackChunkName: "GiftCardConfirmationPage" */ 'components/Pages/GiftCardsPage/Confirmation'))
const GiftCardViewPage = loadable(() => import(/* webpackChunkName: "GiftCardViewPage" */ 'components/Pages/GiftCardsPage/View'))
const MapViewPage = loadable(() => import(/* webpackChunkName: "MapViewPage" */ 'components/Pages/MapViewPage'))
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ 'components/Pages/LandingPage/LandingPage'))
const LandingPageLere = loadable(() => import(/* webpackChunkName: "LandingPageLere" */ 'components/Pages/LandingPage/LandingPageLere'))
const TravelInsurancePage = loadable(() => import(/* webpackChunkName: "TravelInsurancePage" */ 'components/Pages/Insurance/LandingPage'))
const TravelInsuranceCoverGeniusPage = loadable(() => import(/* webpackChunkName: "TravelInsuranceCoverGeniusPage" */ 'components/Pages/Insurance/LandingPage/CoverGenius'))
const TravelInsuranceQuote = loadable(() => import(/* webpackChunkName: "TravelInsuranceQuotePage" */ 'components/Pages/Insurance/QuotePage'))
const TravelInsuranceAddCheckout = loadable(() => import(/* webpackChunkName: "TravelInsuranceAddCheckout" */ 'components/Pages/Insurance/AddCheckoutPage'))
const TravelInsuranceDetails = loadable(() => import(/* webpackChunkName: "TravelInsuranceDetailsPage" */ 'components/Pages/Insurance/DetailsPage'))
const TravelInsurancePayment = loadable(() => import(/* webpackChunkName: "TravelInsurancePayment" */ 'components/Pages/Insurance/PaymentPage'))
const EscapesFinderPage = loadable(() => import(/* webpackChunkName: "EscapesFinderPage" */ 'components/Pages/EscapesFinderPage'))
const ConfirmationPage = loadable(() => import(/* webpackChunkName: "ConfirmationPage" */ 'checkout/Components/Confirmation/ConfirmationPage'))
const BNBLLandingPage = loadable(() => import(/* webpackChunkName: "BNBLLandingPage" */ 'components/Pages/BNBLLandingPage'))
const TWCLandingPage = loadable(() => import(/* webpackChunkName: "TourLandingPage" */ 'components/Pages/TWCLandingPage'))
const EWCLandingPage = loadable(() => import(/* webpackChunkName: "EscapeLandingPage" */ 'components/Pages/EWCLandingPage'))
const ContentHub = loadable(() => import(/* webpackChunkName: "ContentHub" */ 'components/Pages/LandingPage/Hub'))
const OfferPage = loadable(() => import(/* webpackChunkName: "OfferPage" */ 'components/OfferPage'))
const BedBankOfferPage = loadable(() => import(/* webpackChunkName: "OfferPage" */ 'components/Pages/BedbankOfferPage'))
const PaginatedOffersPage = loadable(() => import(/* webpackChunkName: "PaginatedOfferPage" */ 'components/Pages/PaginatedOffersPage'))
const TourV2OfferPage = loadable(() => import(/* webpackChunkName: "TourV2OfferPage" */ 'components/Pages/TourV2/OfferPage/TourV2OfferPage'))
const TourV2OfferVariationPage = loadable(() => import(/* webpackChunkName: "TourV2OfferVariationPage" */ 'components/Pages/TourV2/OfferPage/TourV2OfferVariationPage'))
const TourV2SearchPage = loadable(() => import(/* webpackChunkName: "TourV2SearchPage" */ 'components/Pages/TourV2/SearchPage/TourV2SearchPage'))
const HotelsPage = loadable(() => import(/* webpackChunkName: "HotelsPage" */ 'components/Pages/TypesPage/Hotels/HotelsPage'))
const HomesAndVillasVerticalPage = loadable(() => import(/* webpackChunkName: "HomesAndVillasVerticalPage" */ 'components/Pages/HomeAndVillas/LandingPage/HomesAndVillasVerticalPage'))
const HomesAndVillasSearchPage = loadable(() => import(/* webpackChunkName: "HomesAndVillasSearchPage" */ 'components/Pages/HomeAndVillas/Search/HomesAndVillasSearchPage'))
const HomesAndVillasSearchMapPage = loadable(() => import(/* webpackChunkName: "HomesAndVillasSearchMapPage" */ 'components/Pages/HomeAndVillas/Search/HomesAndVillasSearchMapPage'))
const ToursPage = loadable(() => import(/* webpackChunkName: "ToursPage" */ 'components/Pages/TypesPage/ToursPage'))
const UltraLuxVerticalPage = loadable(() => import(/* webpackChunkName: "UltraLuxVerticalPage" */ 'components/Pages/TypesPage/UltraLux/UltraLuxVerticalPage'))
const LuxBundlesPage = loadable(() => import(/* webpackChunkName: "LuxBundlesVerticalPage" */ 'components/Pages/TypesPage/LuxBundlesPage'))
const DestinationProductPage = loadable(() => import(/* webpackChunkName: "DestinationProductPage" */ 'components/Pages/DestinationProductPage/DestinationProductPage'))
const VFFLandingPage = loadable(() => import(/* webpackChunkName: "VFFLandingPage" */ 'components/Pages/PromotionalPages/VFFLandingPage'))
const KrisFlyerLandingPage = loadable(() => import(/* webpackChunkName: "KrisFlyerLandingPage" */ 'components/Pages/PromotionalPages/KrisFlyerLandingPage'))
const GiftCardsPageContainer = loadable(() => import(/* webpackChunkName: "GiftCardsPage" */ 'components/Pages/GiftCardsPage'))
const KlarnaLandingPage = loadable(() => import(/* webpackChunkName: "KlarnaLandingPage" */ 'components/Pages/PromotionalPages/KlarnaLandingPage'))
const AfterPayLandingPage = loadable(() => import(/* webpackChunkName: "AfterPayLandingPage" */ 'components/Pages/PromotionalPages/AfterPayLandingPage'))
const HoolahLandingPage = loadable(() => import(/* webpackChunkName: "HoolahLandingPage" */ 'components/Pages/PromotionalPages/HoolahLandingPage'))
const RedirectToShopbackPage = loadable(() => import(/* webpackChunkName: "RedirectToShopbackPage" */ 'components/Pages/PromotionalPages/RedirectToShopbackPage'))
const AtomeLandingPage = loadable(() => import(/* webpackChunkName: "AtomeLandingPage" */ 'components/Pages/PromotionalPages/AtomeLandingPage'))
const WaaveLandingPage = loadable(() => import(/* webpackChunkName: "WaaveLandingPage" */ 'components/Pages/PromotionalPages/WaaveLandingPage'))
const LastMinuteEscapesPage = loadable(() => import(/* webpackChunkName: "LastMinuteEscapesPage" */ 'components/Pages/TypesPage//LastMinuteEscapes/LastMinuteEscapesPage'))
const AccountPage = loadable(() => import(/* webpackChunkName: "AccountLandingPage" */ 'components/Pages/AccountPage/AccountLandingPage'))
const ExperienceOfferPage = loadable(() => import(/* webpackChunkName: "ExperienceOfferPage" */ 'components/Experiences/ExperienceOfferPage/ExperienceOfferPage'))
const ExperiencesPage = loadable(() => import(/* webpackChunkName: "ExperiencesPage" */ 'components/Experiences/ExperiencesPage/ExperiencesPage'))
const ExperienceSearchMapPage = loadable(() => import(/* webpackChunkName: "ExperienceSearchMapPage" */ 'components/Experiences/ExperiencesSearch/ExperienceSearchMapPage'))
const CruisesPage = loadable(() => import(/* webpackChunkName: "CruisesPage" */ 'components/Cruises/CruisesPage/CruisesPage'))
const CruiseSearchPage = loadable(() => import(/* webpackChunkName: "CruiseSearchPage" */ 'components/Cruises/SearchPage/CruiseSearchPage'))
const CruiseOfferPage = loadable(() => import(/* webpackChunkName: "CruiseOfferPage" */ 'components/Cruises/CruiseOfferPage/CruiseOfferPage'))
const CruiseLineLandingPage = loadable(() => import(/* webpackChunkName: "CruiseLineLandingPage" */ 'components/Cruises/CruiseLineLandingPage/CruiseLineLandingPage'))
const CruiseDealPage = loadable(() => import(/* webpackChunkName: "CruiseDealPage" */ 'components/Cruises/CruiseDealPage/CruiseDealPage'))
const ExperienceSearchPage = loadable(() => import(/* webpackChunkName: "ExperienceSearchPage" */ 'components/Experiences/ExperiencesSearch/ExperienceSearchPage'))
const TopPicksForMePage = loadable(() => import(/* webpackChunkName: "TopPicksForMePage" */ 'components/Recommendations/TopPicksForMe/TopPicksForMePage'))
const RefereeLandingPage = loadable(() => import(/* webpackChunkName: "RefereeLandingPage" */ '../../../../content/pages/RefereeLanding'))
const GoogleHotelAdPage = loadable(() => import(/* webpackChunkName: "GoogleHotelAdPage" */ '../../../../marketing/pages/GoogleHotelAdPage'))
const CheckoutPageRoutes = loadable(() => import(/* webpackChunkName: "CheckoutPageRoutes" */ 'checkout/routes'))
const CheckoutRestoreCartPage = loadable(() => import(/* webpackChunkName: "CheckoutRestoreCartPage" */ 'checkout/Pages/RestoreCartPage'))
const CheckoutGatewayPage = loadable(() => import(/* webpackChunkName: "CheckoutGatewayPage" */ 'checkout/Pages/GatewayPage'))
const AncillaryPaymentPage = loadable(() => import(/* webpackChunkName: "AncillaryPaymentPage" */ 'checkout/Pages/AncillaryPaymentPage'))
const AncillaryChangeReservationPage = loadable(() => import(/* webpackChunkName: "ChangeReservationPage" */ 'checkout/Pages/AncillaryChangeReservationPage'))
const ExperienceVoucher = loadable(() => import(/* webpackChunkName: "ExperienceVoucher" */ 'components/Pages/ExperienceVoucher'))
const PropertyPage = loadable(() => import(/* webpackChunkName: "PropertyPage" */ '../../../../marketing/pages/PropertyPage'))
const SplashPage = loadable(() => import(/* webpackChunkName: "SplashPage" */ 'components/Pages/SplashPage'))
const RedeemGifted = loadable(() => import(/* webpackChunkName: "RedeemGifted" */ 'components/Pages/RedeemGifted'))
const AuCompetitionsPage = loadable(() => import(/* webpackChunkName: "AuCompetitionsPage" */ '../../../../marketing/pages/AuCompetitionPage'))
const IntlCompetitionsPage = loadable(() => import(/* webpackChunkName: "IntlCompetitionsPage" */ '../../../../marketing/pages/INTLCompetitionPage'))
const ukCompetitionsPage = loadable(() => import(/* webpackChunkName: "UKCompetitionsPage" */ '../../../../marketing/pages/UKCompetitionPage'))
const LETVS6CompetitionPage = loadable(() => import(/* webpackChunkName: "CompetitionPage" */ '../../../../marketing/pages/LETVS6CompetitionPage'))
const HolidayTypeLandingPage = loadable(() => import(/* webpackChunkName: "HolidayTypeLandingPage" */ 'components/Pages/HolidayTypeLandingPage'))
const CarHirePage = loadable(() => import(/* webpackChunkName: "CarHirePage" */ 'components/CarHire/CarHirePage/CarHirePage'))
const CarHireSearchPage = loadable(() => import(/* webpackChunkName: "CarHireSearchPage" */ 'components/CarHire/CarHireSearchPage/CarHireSearchPage'))
const HomePage = loadable(() => import(/* webpackChunkName: "HomePage" */ '../../../../home/pages/HomePage/HomePage'))
const ContentfulWebPage = loadable(() => import(/* webpackChunkName: "ContentfulWebPage" */'../../../../marketing/pages/ContentfulWebPage/ContentfulWebPage'))
const LuxAwardPage = loadable(() => import(/* webpackChunkName: "LuxAwardPage" */'../../../../marketing/pages/ContentfulWebPage/LuxAward/LuxAwardPage'))
const LETVLandingPage = loadable(() => import(/* webpackChunkName: "LETVLandingpage" */'../../../../marketing/pages/LETVLandingPage'))
const LETVEpisodePage = loadable(() => import(/* webpackChunkName: "LETVEpisodePage" */'../../../../marketing/pages/LETVEpisodePage'))
const LuxPlusLandingPage = loadable(() => import(/* webpackChunkName: "LuxPlusLandingPage" */ 'luxPlus/pages/LuxPlusLandingPage'))
const LuxPlusAccountPage = loadable(() => import(/* webpackChunkName: "LuxPlusAccountPage" */ 'luxPlus/pages/LuxPlusAccountPage'))
const LoyaltyLandingPage = loadable(() => import(/* webpackChunkName: "LoyaltyLandingPage" */ 'loyalty/pages/LoyaltyLandingPageContainer'))
const ResetPasswordPage = loadable(() => import(/* webpackChunkName: "ResetPasswordPage" */ 'components/Pages/ResetPasswordPage/ResetPasswordPage'))
const FlightDealLandingPage = loadable(() => import(/* webpackChunkName: "FlightDealLandingPage" */ 'components/Flights/FlightDealLandingPage/FlightDealLandingPage'))
const FlightCampaignsLandingPage = loadable(() => import(/* webpackChunkName: "FlightCampaignsLandingPage" */ 'components/Flights/FlightCampaignsLandingPage/FlightCampaignsLandingPage'))
const TravelPreferencesPage = loadable(() => import(/* webpackChunkName: "TravelPreferencesPage" */ 'components/Pages/AccountTravelPreferencesPage/TravelPreferencesPage'))
const AgentHubHomePage = loadable(() => import(/* webpackChunkName: "AgentHubHomepage" */ 'agentHub/pages/AgentHubHomepage'))
const AgentHubAccountSetupCompleted = loadable(() => import(/* webpackChunkName: "AgentHubAccountSetupCompleted" */ 'agentHub/pages/AgentHubAccountSetup/AgentHubAccountSetupCompleted'))
const AgentHubToursPage = loadable(() => import(/* webpackChunkName: "AgentHubToursPage" */ 'agentHub/pages/AgentHubToursPage'))
const SkyCheckConfirmationPage = loadable(() => import(/* webpackChunkName: "SkyCheckConfirmationPage" */ 'components/Pages/EmailConfirmedPage/SkyCheckConfirmation/SkyCheckConfirmationPage'))
const BusinessTravellerFaqPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerFaqPage" */ 'businessTraveller/pages/faq/BusinessTravellerFaqPage'))
const TailorMadeTourRequestPage = loadable(() => import(/* webpackChunkName: "TailorMadeTourRequestPage" */ 'components/Pages/TailorMadeTour/TailorMadeTourRequestPage'))
const TailorMadeTourRequestConfirmationPage = loadable(() => import(/* webpackChunkName: "TailorMadeTourRequestConfirmationPage" */ 'components/Pages/TailorMadeTour/TailorMadeTourRequestConfirmationPage'))
const SecurePaymentPage = loadable(() => import(/* webpackChunkName: "SecurePaymentPage" */ 'components/Pages/SecurePaymentPage'))
const WhitelabelHomePage = loadable(() => import(/* webpackChunkName: "WhitelabelHomePage" */ 'whitelabel/pages/WhitelabelHomePage'))
const TravelCompanionsPage = loadable(() => import(/* webpackChunkName: "TravelCompanionPage" */ 'components/Pages/TravelCompanionsPage'))
const UserCartPage = loadable(() => import(/* webpackChunkName: "UserCartPage" */ 'components/Pages/UserCartPage/UserCartPage'))
const TourCataloguePage = loadable(() => import(/* webpackChunkName: "TourCatalogPage" */ 'components/Pages/TourV2/TourCataloguePage'))
const MarketplacePage = loadable(() => import(/* webpackChunkName: "HotelsPage" */ 'components/Pages/MarketplacePage/MarketplacePage'))

const promotionalPages: Record<string, LoadableComponent<any>> = {
  VFFLandingPage,
  KrisFlyerLandingPage,
  GiftCardsPageContainer,
  KlarnaLandingPage,
  AfterPayLandingPage,
  HoolahLandingPage,
  RedirectToShopbackPage,
  AtomeLandingPage,
  WaaveLandingPage,
}

const onlyTour: Array<App.LEContractedOfferType> = [OFFER_TYPE_TOUR]
const onlyLastMinute: Array<App.LEContractedOfferType> = [OFFER_TYPE_LAST_MINUTE]
const onlyHotel: Array<App.LEContractedOfferType> = [OFFER_TYPE_HOTEL]
const mapPageFilters: App.OfferListFilters = {
  limit: TYPE_PAGE_LIMIT,
}
const ultraLuxOfferTypes: Array<App.LEContractedOfferType> = [OFFER_TYPE_HOTEL, 'tactical_ao_hotel']
const ultraLuxMapPageFilters: App.OfferListFilters = {
  ...mapPageFilters,
  propertySubCategories: ['Ultra Lux'],
}

interface Props {
  currentRegionCode: string
  standaloneFlightsEnabled: boolean
  standaloneCruiseEnabled: boolean
  isLuxPlusEnabled: boolean
  isLoyaltyEnabled: boolean
  isStandaloneAirportTransfersEnabled: boolean
  tenant: string
}

function PageRoutes(props: Props) {
  const {
    currentRegionCode,
    standaloneFlightsEnabled,
    standaloneCruiseEnabled,
    isLuxPlusEnabled,
    isLoyaltyEnabled,
    isStandaloneAirportTransfersEnabled,
  } = props

  const hasSetupAgentHubAccount = useAppSelector(state => state.agentHub.agentHubAccount.account?.status === 'onboarded')

  return (
    <Switch>
      {/* This blocks access to all pages but the onboarding for agent hub until the user has signed up */}
      {config.agentHub.isEnabled && !hasSetupAgentHubAccount && <AgentHubGuestPageRoutes />}

      {/* Tour v1 redirects */}
      {Object.keys(config.toursV1Redirects).map(tourV1 =>
        <RedirectWithStatus
          key={tourV1}
          code={301}
          from={`/:regionCode/offer/${tourV1}`}
          to={`/tour/${config.toursV1Redirects[tourV1]}`}
          withSearchAndHash={false}
        />,
      )}

      {/* Offer list Pages */}
      {config.agentHub.isEnabled && <Route exact path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} component={AgentHubToursPage} />}
      <Route exact path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} component={ToursPage} />
      <Route path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}/map-view`}>
        <MapViewPage
          listViewPath={`/${OFFER_TYPE_TOUR_SLUG}`}
          offerTypes={onlyTour}
        />
      </Route>
      <Route exact path={`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}`} component={HotelsPage} />
      <Route path={`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}/map-view`}>
        <MapViewPage listViewPath={`/${OFFER_TYPE_HOTEL_SLUG}`}
          offerTypes={onlyHotel}
          filters={mapPageFilters}
        />
      </Route>
      <Route exact path={`/:regionCode/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`} component={LuxBundlesPage} />
      {config.ULTRALUX_ENABLED && <Route exact path={`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}`} component={UltraLuxVerticalPage} />}
      {config.ULTRALUX_ENABLED && <Route path={`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}/map-view`}>
        <MapViewPage
          listViewPath={`/${PRODUCT_TYPE_ULTRALUX_SLUG}`}
          offerTypes={ultraLuxOfferTypes}
          filters={ultraLuxMapPageFilters}
          heading={<>Explore our <i>ultra lux</i> offers</>}
        />
      </Route>}
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path="/:regionCode/experiences" component={ExperiencesPage} />}

      {/* Cruises */}
      {standaloneCruiseEnabled && <Route exact path="/:regionCode/cruises" component={CruisesPage} />}
      <Route path="/:regionCode/cruises/departures/:departureLocation" component={CruiseLocationLandingPage} />
      <Route path="/:regionCode/cruises/destinations/:destinationLocation" component={CruiseLocationLandingPage} />
      <Route path="/:regionCode/cruises/ships/:shipNames" component={CruiseShipsLandingPage} />
      {standaloneCruiseEnabled && <Route exact path="/:regionCode/cruises/deal/:slug" component={CruiseDealPage} />}
      {standaloneCruiseEnabled && <Route exact path="/:regionCode/cruises/:offerId" component={CruiseOfferPage} />}
      {standaloneCruiseEnabled && <Route exact path="/:regionCode/cruises/:offerSlug/:offerId" component={CruiseOfferPage} />}
      {standaloneCruiseEnabled && <Route exact path="/:regionCode/cruise-line/:cruiseLineSlug" component={CruiseLineLandingPage} />}

      {/* Car Hire Pages */}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && <Route exact path="/:regionCode/car-hire" component={CarHirePage} />}

      {/* Offer pages */}
      <Route exact path={`/:regionCode/${PRODUCT_TYPE_HOME_AND_VILLAS_SLUG}`} component={HomesAndVillasVerticalPage} />
      <Route exact path="/:regionCode/offer/:offerId">
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </Route>
      <Route path="/:regionCode/offer/:offerSlug/:offerId">
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </Route>
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path="/:regionCode/experience/:id">
        {({ match }) => <ExperienceOfferPage key={match?.params.offerId} />}
      </Route>}
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route path="/:regionCode/experience/:slug/:id">
        {({ match }) => <ExperienceOfferPage key={match?.params.offerId} />}
      </Route>}
      <Route path="/:regionCode/partner/:offerSlug/:offerId">
        {({ match }) => <BedBankOfferPage key={match?.params.offerId} />}
      </Route>
      <Route exact path={`/:regionCode/${OFFER_TYPE_LAST_MINUTE_SLUG}`} component={LastMinuteEscapesPage} />
      <Route path={`/:regionCode/${OFFER_TYPE_LAST_MINUTE_SLUG}/map-view`}>
        <MapViewPage
          listViewPath={`/${OFFER_TYPE_LAST_MINUTE_SLUG}`}
          offerTypes={onlyLastMinute}
          filters={mapPageFilters}
        />
      </Route>
      <Route path="/:regionCode/page/:pageNum" component={PaginatedOffersPage} />
      <Route path="/:regionCode/map-view">
        <MapViewPage
          offerTypes={FRONT_PAGE_MAP_VIEW_OFFERS_TYPES(currentRegionCode)} listViewPath="/"
          filters={mapPageFilters}
        />
      </Route>
      // If brand defines a particular default homepage, redirect "/" to that location
      {
        config.defaultHomePage && (
          <Route
            exact
            path="/:regionCode/"
            component={({ location }: RouteComponentProps) => (
              <RedirectWithStatus
                code={302}
                to={location.pathname.replace(/$/, config.defaultHomePage)}
              />
            )
            }
          />
        )
      }

      {config.agentHub.isEnabled && <Route exact path="/:regionCode/" component={AgentHubHomePage} />}
      {config.WHITELABEL_HOME_PAGE_ENABLED && <Route exact path="/:regionCode/" component={WhitelabelHomePage} />}

      <Route exact path="/:regionCode/" component={HomePage} />
      <RedirectWithStatus code={301} from="/:regionCode/villas" to="/:regionCode/holidays/homes-and-villas" />

      {/* Support Pages */}
      {config.SUPPORT_ENABLED && <Route path="/:regionCode/support" component={SupportPage} />}

      {/* Sales Team Contact Page */}
      <Route path="/:regionCode/request-video-chat/:offerId/:packageId" component={VideoChatPage} />
      {config.ENABLE_LETV_S6_SPIN_COMPETITION_PAGE && <Route path="/:regionCode/tv/season-6/competition" component={LETVS6CompetitionPage} />}

      {/* LuxPlus */}
      {isLuxPlusEnabled &&
        <Route path={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} component={LuxPlusLandingPage} />
      }
      {isLuxPlusEnabled &&
        <RedirectWithStatus code={301} from="/:regionCode/luxplus" to={`/:regionCode/${LUXURY_PLUS_PATHS.LANDING_PAGE}`}/>
      }

      {/* Loyalty */}
      {isLoyaltyEnabled &&
        <Route path={`/:regionCode/${LOYALTY_LANDING_PAGE}`} component={LoyaltyLandingPage} />
      }
      {isLoyaltyEnabled &&
        <RedirectWithStatus code={301} from="/:regionCode/society" to={`/:regionCode/${LOYALTY_LANDING_PAGE}`}/>
      }
      {isLoyaltyEnabled &&
        <RedirectWithStatus code={301} from="/:regionCode/société" to={`/:regionCode/${LOYALTY_LANDING_PAGE}`}/>
      }

      {/* Search Pages */}
      <Route path="/:regionCode/search-fullscreen" component={HotelSearchFullScreenPage} />
      <Route exact path="/:regionCode/search" component={HotelSearchPage} />
      <Route exact path="/:regionCode/search/map" component={HotelSearchMapPage} />
      <Route exact path="/:regionCode/search/homes-and-villas" component={HomesAndVillasSearchPage} />
      <Route exact path="/:regionCode/search/homes-and-villas/map" component={HomesAndVillasSearchMapPage} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path="/:regionCode/search/experiences" component={ExperienceSearchPage} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path="/:regionCode/search/experiences/map" component={ExperienceSearchMapPage} />}
      {config.CRUISES_STANDLONE_ENABLED && <Route exact path="/:regionCode/search/cruises" component={CruiseSearchPage} />}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && <Route exact path="/:regionCode/search/car-hire" component={CarHireSearchPage} />}

      <RedirectWithStatus code={301} from="/:regionCode/amex" to="/:regionCode/" />
      <RedirectWithStatus code={301} from="/:regionCode/refund-policy" to="/:regionCode/booking-policies" />
      <RedirectWithStatus code={301} from="/:regionCode/frequently-asked-questions" to="/:regionCode/support" />

      {/* Account pages */}
      <RedirectWithStatus code={301} from="/:regionCode/account/my-destinations" to="/:regionCode/account" />
      <AuthRoute exact path="/:regionCode/account" component={AccountPage} />
      <AuthRoute exact path="/:regionCode/account/credits-payments" component={CreditsAndPaymentPage} />
      <AuthRoute showPopup path="/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher" component={ExperienceVoucher} />
      <AuthRoute showPopup exact path="/:regionCode/account/my-escapes/order/:orderId" component={OrderPage} />
      <AuthRoute exact path="/:regionCode/account/travel-preferences" component={TravelPreferencesPage} />
      <AuthRoute exact path="/:regionCode/account/travel-companions" component={TravelCompanionsPage} />
      {isStandaloneAirportTransfersEnabled &&
        <AuthRoute exact path="/:regionCode/account/my-escapes/order/:orderId/airport-transfers" component={AirportTransferPage} />
      }

      {/* Apps are still pointing to /my-escapes, so redirect to the updated route */}
      <RedirectWithStatus code={301} from="/:regionCode/my-escapes/:modalType?" to="/:regionCode/account/my-escapes/:modalType?" />
      <AuthRoute showPopup path="/:regionCode/account/my-escapes/:tab(upcoming|cancelled|past|approval-requests)?" component={MyEscapesPage} />
      <AuthRoute exact path="/:regionCode/account/details/traveller" component={AccountDetailsTravellerPage} />
      <AuthRoute exact path="/:regionCode/account/details" component={AccountDetailsPage} showPopup />
      <AuthRoute path="/:regionCode/booking-form/:orderId?" component={TourTravellerInfoPage} />
      <AuthRoute showPopup exact path="/:regionCode/review/:orderId?" component={UserReviews} />
      <Route exact path="/:regionCode/reset-password/:resetPasswordToken" component={ResetPasswordPage} />
      {isLuxPlusEnabled && <AuthRoute showPopup exact path={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} component={LuxPlusAccountPage} />}

      {/* hidden preference centre page */}
      <AuthRoute showPopup path="/:regionCode/account/hidden-communication-preferences" component={config.BRAND === LUXURY_ESCAPES ? MySubscriptionsPage : MyWLSubscriptionsPage} />

      {/* Subscriptions Pages */}
      <AuthRoute path="/:regionCode/account/communication-preferences" component={PreferenceCentrePage} />
      <RedirectWithStatus code={301} from="/:regionCode/my-subscriptions" to="/:regionCode/account/communication-preferences" />
      <RedirectWithStatus code={301} from="/:regionCode/account/my-subscriptions" to="/:regionCode/account/communication-preferences" />
      <RedirectWithStatus code={301} from="/:regionCode/account/subscriptions" to="/:regionCode/account/communication-preferences" />

      <RedirectWithStatus code={301} exact from="/:regionCode/account/payment-cards" to="/:regionCode/account/credits-payments" />
      <RedirectWithStatus code={301} from="/:regionCode/account-details" to="/:regionCode/account/details" />

      {/* Insurance pages */}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/quote" component={TravelInsuranceQuote} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/add" component={TravelInsuranceAddCheckout} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/details" component={TravelInsuranceDetails} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path="/:regionCode/travel-protection/payment" component={TravelInsurancePayment} />}
      <RedirectWithStatus code={301} from="/:regionCode/insurance/checkout" to="/:regionCode/travel-protection/quote" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/confirmation" to="/:regionCode/booking/confirmation" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance" to="/:regionCode/travel-protection" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/xcover" to="/:regionCode/travel-protection/xcover" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/quote" to="/:regionCode/travel-protection/quote" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/add" to="/:regionCode/travel-protection/add" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/details" to="/:regionCode/travel-protection/details" />
      <RedirectWithStatus code={301} from="/:regionCode/insurance/payment" to="/:regionCode/travel-protection/payment" />

      {/* Gift card pages */}
      <RedirectWithStatus code={301} from="/:regionCode/giftcards" to="/:regionCode/gift-cards" />
      <RedirectWithStatus code={301} from="/:regionCode/giftcard" to="/:regionCode/gift-cards" />
      <RedirectWithStatus code={301} from="/:regionCode/gift-card" to="/:regionCode/gift-cards" exact />
      <AuthRoute path="/:regionCode/gift-cards/confirmation" component={GiftCardConfirmationPage} />
      <AuthRoute showPopup path="/:regionCode/gift-card/:code" component={GiftCardViewPage} />

      {/* Booking pages */}
      <AuthRoute showPopup path="/:regionCode/booking/confirmation" component={ConfirmationPage} />
      <AuthRoute showPopup path="/:regionCode/bb/booking/confirmation" component={ConfirmationPage} />
      <RedirectWithStatus code={301} from="/:regionCode/confirmation/:orderId" to="/:regionCode/booking/confirmation?orderId=:orderId" />
      <RedirectWithStatus code={301} from="/:regionCode/bb/booking/confirmation" to="/:regionCode/booking/confirmation" />
      <RedirectWithStatus code={301} from="/:regionCode/bb/booking/:step" to="/:regionCode/booking/:step" />

      {/* Redeem gift hotel and experience */}
      <Route exact path="/:regionCode/redeem/:redeemCode/:orderId/:offerId" component={RedeemGifted} />

      {/* TourV2Pages */}
      <Route exact path="/:regionCode/tour/:offerId">
        {({ match }) => <TourV2OfferPage key={match?.params.offerId} />}
      </Route>

      <Route exact path="/:regionCode/tour/:offerId/:variationId" component={TourV2OfferVariationPage} />

      <Route exact path="/:regionCode/search/tours" component={TourV2SearchPage} />

      {/* Tour signature series contentful redirects */}
      <RedirectWithStatus code={301} from="/:regionCode/signature-series.p1" to="/:regionCode/campaign/signature-series" />

      {/* Tailor-Made Tours */}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && (
        <Route exact path="/:regionCode/tailor-made-tours/requests/:requestId" component={TailorMadeTourRequestConfirmationPage} />
      )}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && (
        <Route exact path="/:regionCode/tailor-made-tours/requests" component={TailorMadeTourRequestPage} />
      )}

      <Route exact path="/:regionCode/tours/tours-catalogue" component={TourCataloguePage} />

      {config.hasReferralProgram && (
        <Route exact path="/:regionCode/refer" component={ReferPage} />
      )}
      {config.hasReferralProgram && (
        <Route path="/:regionCode/refer/:referralCode" component={RefereeLandingPage} />
      )}

      <Route path="/:regionCode/google-hotel-ads" component={GoogleHotelAdPage} />
      <RedirectWithStatus code={302} from="/:regionCode/property/ayodya-resort-bali-bedbank:6486e2f1-958e-4f91-a5b0-481205904898" to="/:regionCode/offer/ayodya-resort-bali-nusa-dua-indonesia-asia/0062y00000JN0RvAAL" />
      <RedirectWithStatus code={302} from="/:regionCode/abercrombie-and-kent-tours.p1" to="/:regionCode/tours" />

      <Route path="/:regionCode/property/:propertyNameAndId" component={PropertyPage} />
      <Route exact path="/:regionCode/tv/season-6/:episode" component={LETVEpisodePage} />
      <Route exact path="/:regionCode/tv/season-6" component={LETVLandingPage} />
      {config.ENABLE_AU_COMPETITION && <Route path="/:regionCode/hunter-valley-competition" component={AuCompetitionsPage} />}
      {config.ENABLE_INTL_COMPETITION && <Route path="/:regionCode/spin-the-wheel-competition" component={IntlCompetitionsPage} />}
      <Route path="/:regionCode/NHCollectionDubaiThePalm" component={ukCompetitionsPage} />
      <RedirectWithStatus code={301} from="/:regionCode/citi" to="/:regionCode/save-with-citi.p1" />
      <RedirectWithStatus code={301} from="/:regionCode/terms" to="/:regionCode/terms-and-conditions" />

      <Route path="/:regionCode/sharethis/verify" component={(): null => {
        const mainUrl = `https://platform-api.sharethis.com/js/sharethis.js#property=${config.GOOGLE_APP_ID}`
        const script = document.createElement('script')
        script.setAttribute('id', 'sharethis-js')
        script.src = mainUrl
        script.async = true
        document.body.appendChild(script)
        return null
      }}
      />

      {/* Content hub pages */}
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <RedirectWithStatus code={301} from="/:regionCode/pages/:slug" to="/:regionCode/:slug.p1" />
      }
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <Route exact path="/:regionCode/:slug.p1" component={ContentHub} />
      }

      {config.staticPages.map(sp => (
        sp.url?.to && <Route
          exact
          key={sp.url.to}
          path={`/:regionCode${sp.url.to}`}
          render={props => {
            if (sp.type === 'bestPrice') {
              return <BestPricePage />
            } else if (sp.type === 'aboutUs') {
              return <AboutUsPage />
            } else if (sp.type === 'careersAndCulture') {
              return <CareersPage />
            }
            else if (sp.type === 'termsAndConditions') {
              return <LegalPages slug="terms-and-conditions" location={props.location} />
            } else if (sp.type === 'luxPlusTermsAndConditions') {
              return <LegalPages slug={LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE} location={props.location} />
            } else if (sp.type === 'societeTermsAndConditions') {
              return <LegalPages slug="societe-terms-and-conditions" location={props.location} />
            } else if (sp.type === 'societeFAQs') {
              return <LegalPages slug="societe-frequently-asked-questions" location={props.location} />
            } else if (sp.type === 'refundPolicy') {
              return <LegalPages slug="refund-policy" location={props.location} />
            } else if (sp.type === 'news') {
              return <NewsPage slug="news" location={props.location} />
            } else if (sp.type === 'privacyPolicy') {
              return <LegalPages slug="privacy-policy" location={props.location} />
            } else if (sp.type === 'giftcardTermsAndConditions') {
              return <LegalPages slug="giftcards-terms" location={props.location} />
            } else if (sp.type === 'flights') {
              return <LegalPages slug="flights-policies" location={props.location} />
            } else if (sp.type === 'frequentFlyerPrograms') {
              return <FrequentFlyerPage slug="frequent-flyer-programs" location={props.location} />
            } else if (sp.type === 'specialProductsPromotions') {
              return <LegalPages slug="promotions" location={props.location} />
            } else if (sp.type === 'bookingPolicies') {
              return <LegalPages slug="booking-policies" location={props.location} />
            } else if (sp.type === 'bestPriceTerms') {
              return <LegalPages slug="best-price-guarantee-terms" location={props.location} />
            } else if (sp.type === 'feeSchedule') {
              return <LegalPages slug="fee-schedule" location={props.location} />
            } else if (sp.type === 'travelInsurance') {
              return <TravelInsurancePage />
            } else if (sp.type === 'travelInsuranceCoverGenius') {
              return <TravelInsuranceCoverGeniusPage />
            } else if (sp.type === 'bnblLanding') {
              return <BNBLLandingPage />
            } else if (sp.type === 'twcLanding') {
              return <TWCLandingPage />
            } else if (sp.type === 'ewcLanding') {
              return <EWCLandingPage />
            } else if (sp.type === 'businessFaq') {
              return <BusinessTravellerFaqPage />
            } else if (sp.type === 'affiliatePage') {
              return <ContentfulWebPage />
            } else {
              return <StaticPage type={sp.type} title={sp.text} />
            }
          }}
        />
      ))}

      {/* Promotional Pages */}
      {!standaloneFlightsEnabled && <Route path="/:regionCode/flights" component={FlightsLandingPage} />}
      {standaloneFlightsEnabled && <Route exact path="/:regionCode/flights/:dealCategory?" component={FlightsSearchLandingPage} />}
      {standaloneFlightsEnabled && <Route path="/:regionCode/flights-search" component={FlightsSearchPage} />}
      {standaloneFlightsEnabled && <Route path="/:regionCode/flights-search-results" component={FlightsSearchResultsPage} />}
      {standaloneFlightsEnabled && <Route path="/:regionCode/flights-search-results-multi-city" component={FlightsSearchMultiCityResultsPage} />}
      <Route path="/:regionCode/flight/deal/:id" component={FlightDealLandingPage} />
      <Route path="/:regionCode/flights/campaigns/:id" component={FlightCampaignsLandingPage} />
      <Route path="/:regionCode/escapes-finder" component={EscapesFinderPage} />
      <RedirectWithStatus from="/:regionCode/kris-flyer" to="/:regionCode/krisflyer" />
      <RedirectWithStatus code={301} from="/:regionCode/disneyland-offers" to="/:regionCode/experience/led%3A0062y00000Gui6KAAR" />
      <RedirectWithStatus code={301} from="/:regionCode/disneyland-offers-test" to="/:regionCode/experience/led%3A0062y000009EEOEAA4" />

      {config.promotionalPages.map(pp => {
        const PromotionalComponent = promotionalPages[pp.component]
        return <Route
          key={pp.url}
          path={`/:regionCode${pp.url}`}
        >
          <PromotionalComponent />
        </Route>
      })}
      {config.holidayPages &&
        <RedirectWithStatus code={301} from="/:regionCode/holidays/:slug" to="/:regionCode/:slug.h1" />
      }
      {config.holidayPages &&
        <Route path="/:regionCode/:slug.h1" component={(props: ComponentProps<typeof LandingPage>) => <LandingPage {...props} typeOfPage={TYPE_OF_PAGE_HOLIDAY} />} />
      }
      {config.destinationPages &&
        <RedirectWithStatus code={301} from="/:regionCode/:slug.d1" to="/:regionCode/destination/:slug" />
      }
      {config.destinationPages &&
        <Route path="/:regionCode/:slug.d1.lere" component={(props: ComponentProps<typeof LandingPageLere>) => <LandingPageLere {...props} typeOfPage={TYPE_OF_PAGE_DESTINATION} />} />
      }
      <RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting" to="/:regionCode/campaign/lux-list-travel-awards" />
      <RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting-uk" to="/:regionCode/campaign/lux-list-travel-awards-uk" />
      <RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting-us" to="/:regionCode/campaign/lux-list-travel-awards-us" />
      {/* Redirect due to wrong URL sent out */}
      <RedirectWithStatus code={301} from="/:regionCode/campaign/singapore/competition-april" to="/:regionCode/campaign/competition-april" />
      <Route exact path="/:regionCode/campaign/lux-list-travel-awards" component={LuxAwardPage} />
      <Route exact path="/:regionCode/campaign/lux-list-travel-awards-prizes" component={LuxAwardPage} />
      <Route exact path="/:regionCode/campaign/lux-list-travel-awards-us" component={LuxAwardPage} />
      <Route exact path="/:regionCode/campaign/lux-list-travel-awards-prizes-us" component={LuxAwardPage} />
      <Route exact path="/:regionCode/campaign/lux-list-travel-awards-uk" component={LuxAwardPage} />
      <Route exact path="/:regionCode/campaign/lux-list-travel-awards-prizes-uk" component={LuxAwardPage} />
      <Route path="/:regionCode/destination" component={ContentfulWebPage} />
      <Route path="/:regionCode/campaign" component={ContentfulWebPage} />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/maldives" to="/:regionCode/destination/maldives" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/bali" to="/:regionCode/destination/bali" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/greece" to="/:regionCode/destination/greece" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels/country/spain" to="/:regionCode/destination/spain" />

      {/* New URL Structure For SEO Destination/Product pages */}
      <Route path="/:regionCode/:productType/country/:country" component={DestinationProductPage} />
      <Route path="/:regionCode/:productType/city/:city/:state/:country" component={DestinationProductPage} />
      <Route path="/:regionCode/:productType/city/:city/:country" component={DestinationProductPage} />
      <Route path="/:regionCode/:productType/location/:location/:city/:state/:country" component={DestinationProductPage} />
      <Route path="/:regionCode/:productType/location/:location/:city/:country" component={DestinationProductPage} />
      <Route path="/:regionCode/:productType/location/:location/:country" component={DestinationProductPage} />

      {/* HolidayPackage Pages: Rule of URL: [base]/holiday-packages/:location/:holidayType */}
      <Route path="/:regionCode/holiday-packages/:location/:holidayType" component={DestinationLandingPage} />
      <Route path="/:regionCode/holiday-packages/:location" component={DestinationLandingPage} />
      <Route path="/:regionCode/resorts/:location" component={DestinationLandingPage} />

      {/* SEO Destination/Product pages */}
      <Route path="/:regionCode/:location.:holidayType.:productType" component={DestinationProductPage} />
      <Route path="/:regionCode/:locationOrHolidayType.:productType" component={DestinationProductPage} />

      {config.LERE_ENABLE_TOP_PICKS_FOR_ME && <Route path="/:regionCode/top-picks-for-me" component={TopPicksForMePage} />}

      <AuthRoute
        showPopup
        path="/:regionCode/ancillary-payment/:orderId/:type(deposit-balance|instalment-balance|instalment|reserve-balance|custom-offer|payment-schedule-balance)"
        component={AncillaryPaymentPage}
      />
      <AuthRoute
        showPopup
        path="/:regionCode/ancillary-change-reservation/:orderId/:type(change-dates|change-package|tour-optional-experiences)/:itemId?"
        component={AncillaryChangeReservationPage}
      />

      <AuthRoute showPopup path="/:regionCode/checkout-restore-cart" component={CheckoutRestoreCartPage} />
      <AuthRoute showPopup path="/:regionCode/checkout-gateway" component={CheckoutGatewayPage} />
      <Route exact path="/:regionCode/checkout/:cartId/:step?" component={CheckoutPageRoutes} />
      <Route path="/:regionCode/checkout/:step?" component={CheckoutPageRoutes} />
      <BaseRoute path="/:regionCode/trip-planner">
        <TripPlannerRoutes />
      </BaseRoute>
      <AuthRoute showPopup path="/:regionCode/cart" component={UserCartPage} />
      <RedirectWithStatus code={301} from="/:regionCode/user-cart" to="/:regionCode/cart" />

      <Route path="/:regionCode/splashpage" component={SplashPage}/>
      <Route exact path="/:regionCode/hotels/:holidayType" component={HolidayTypeLandingPage} />
      <Route exact path="/:regionCode/hotels/:holidayType/:country" component={HolidayTypeLandingPage} />
      <Route exact path="/:regionCode/hotels/:holidayType/:country/:cityOrState" component={HolidayTypeLandingPage} />

      {config.SECURE_PAYMENT_ENABLED &&
        <Route exact path="/:regionCode/pay/:paymentLinkId" component={SecurePaymentPage} />
      }

      {config.agentHub.isEnabled && hasSetupAgentHubAccount &&
        <Switch>
          <Route exact path="/:regionCode/agent-hub/account-setup-completed" component={AgentHubAccountSetupCompleted} />
          <RedirectWithStatus
            from="/:regionCode/agent-hub/complete-account-setup"
            to="/:regionCode/agent-hub/account-setup-completed"
          />
        </Switch>
      }

      {config.SPOOFING_SEND_CONFIRMATION_EMAIL && <Route path="/:regionCode/skycheck" component={SkyCheckConfirmationPage} />}

      {config.businessTraveller.isEnabled && <BusinessTravellerPageRoutes/>}

      {!!useOptimizelyExperiment(OptimizelyFeatureFlags.marketplacePage) && config.marketplace.isEnabled &&
        <Route exact path="/:regionCode/marketplace" component={MarketplacePage} />
      }

      {/* Mobile app marketing url, redirect this to home page until we have one */}
      <RedirectWithStatus code={302} from="/:regionCode/app" to="/:regionCode/" />

      <Route>
        <HttpStatus code={404}>
          <Error404PageWithOffers />
        </HttpStatus>
      </Route>
    </Switch>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currentRegionCode: state.geo.currentRegionCode,
    isLuxPlusEnabled: isLuxPlusEnabled(state),
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state) || isFlightsCredit(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
    isStandaloneAirportTransfersEnabled: getStandaloneAirportTransfersEnabled(state),
  }
}

export default connect(mapStateToProps)(PageRoutes)
